import React, { useState } from 'react';
import { Download, Trash2, File, Image, FileText, FileSpreadsheet, Eye, AlertCircle } from 'lucide-react';
import Button from './Button';
import IconButton from './IconButton';
import Modal from './Modal';
import { formatDateTime } from '../../../utils/dateUtils';

const AttachmentsList = ({
  attachments,
  onDelete,
  onDownload,
  onPreview,
  showActions = true,
  className = ''
}) => {
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const getFileIcon = (attachment) => {
    const { mime_type, attachment_type, original_filename } = attachment;
    const extension = original_filename.split('.').pop()?.toUpperCase() || '?';

    const renderIconWithText = (IconComponent, colorClass, text) => (
      <div className="flex flex-col items-center justify-center gap-1 w-10">
        <IconComponent className={`w-6 h-6 ${colorClass}`} />
        <span className={`text-[10px] font-bold ${colorClass} leading-none truncate max-w-full`}>{text}</span>
      </div>
    );
    
    if (mime_type.startsWith('image/')) {
      return renderIconWithText(Image, "text-blue-500", extension);
    }
    if (mime_type === 'application/pdf') {
      return renderIconWithText(FileText, "text-red-500", "PDF");
    }
    if (attachment_type === 'spreadsheet' || mime_type.includes('excel') || mime_type.includes('csv')) {
      return renderIconWithText(FileSpreadsheet, "text-green-500", extension);
    }
    if (attachment_type === 'igc') {
      return renderIconWithText(File, "text-purple-500", "IGC");
    }
    return renderIconWithText(File, "text-gray-500", extension);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };


  const handleDelete = async (attachmentId) => {
    if (!onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(attachmentId);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete attachment:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = (attachment) => {
    if (onDownload) {
      onDownload(attachment.id, attachment.original_filename);
    } else {
      // Default download behavior - open in new tab
      window.open(`/uploads/repairs/${attachment.filename}`, '_blank');
    }
  };

  const handlePreview = async (attachment) => {
    if (!attachment.mime_type.startsWith('image/')) {
      // For non-images, just download/open
      handleDownload(attachment);
      return;
    }

    // Open the modal straight away so the click always gives visible feedback.
    setPreviewOpen(true);
    setPreviewImage(null);
    setPreviewError(null);

    if (!onPreview) {
      setPreviewError('Image preview is not available here.');
      return;
    }

    setPreviewLoading(true);
    try {
      setPreviewImage(await onPreview(attachment.id));
    } catch (error) {
      console.error('Failed to load image preview:', error);
      setPreviewError('Failed to load image preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewImage(null);
    setPreviewError(null);
  };

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
        >
          {/* File Icon */}
          <div className="flex-shrink-0">
            {getFileIcon(attachment)}
          </div>

          {/* File Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="text-sm font-medium text-gray-900 truncate" title={attachment.original_filename}>
                {attachment.original_filename}
              </h4>
              
              {/* Actions */}
              {showActions && (
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  {/* Preview/View Button (for images) */}
                  {attachment.mime_type.startsWith('image/') && (
                    <IconButton
                      icon={Eye}
                      onClick={() => handlePreview(attachment)}
                      variant="ghost"
                      size="sm"
                      title="Preview image"
                    />
                  )}

                  {/* Download Button */}
                  <IconButton
                    icon={Download}
                    onClick={() => handleDownload(attachment)}
                    variant="ghost"
                    size="sm"
                    title="Download file"
                  />

                  {/* Delete Button */}
                  {onDelete && (
                    <IconButton
                      icon={Trash2}
                      onClick={() => setDeleteConfirm(attachment.id)}
                      variant="ghost"
                      size="sm"
                      title="Delete attachment"
                      className="text-red-500 hover:text-red-700"
                    />
                  )}
                </div>
              )}
            </div>

            <div className="mt-1 text-xs text-gray-500 leading-relaxed truncate">
              {formatFileSize(attachment.file_size)} • {(() => {
                const dateStr = formatDateTime(attachment.created_at);
                const isRelative = dateStr.includes('ago') || dateStr === 'Just now' || dateStr === 'Yesterday';
                return isRelative ? dateStr : dateStr;
              })()}
            </div>
          </div>
        </div>
      ))}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Modal
          isOpen={true}
          onClose={() => setDeleteConfirm(null)}
          title="Delete Attachment"
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-900">
                  Are you sure you want to delete this attachment?
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Image Preview Modal */}
      {previewOpen && (
        <Modal
          isOpen={true}
          onClose={closePreview}
          title="Image Preview"
          size="lg"
        >
          <div className="text-center">
            {previewLoading && (
              <div className="inline-flex items-center gap-2 text-gray-500 py-8">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                Loading image...
              </div>
            )}

            {previewError && (
              <div className="flex items-center justify-center gap-2 py-8 text-red-600">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{previewError}</span>
              </div>
            )}

            {previewImage && !previewError && (
              <img
                src={previewImage}
                alt="Attachment preview"
                className="max-w-full max-h-96 mx-auto rounded-lg"
                onError={() => setPreviewError('Failed to load image preview.')}
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AttachmentsList;
